import ReactTooltip from 'react-tooltip';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import noop from 'lodash/noop';
import { CloseIcon } from 'icons/outline';

type ActionButtonTooltipProps = {
  id: string;
  className: string;
  dataTip: ReactNode;
  children: ReactNode;
  alwaysShow: boolean;
  background: 'dark' | 'light';
  textSize?: string;
  clickable?: boolean;
  delayHide?: number;
  onClose?: () => void;
};
export const ActionButtonTooltip = ({
  id,
  dataTip = 'Tooltip',
  children = 'Tooltip Content',
  clickable = false,
  delayHide = 0,
  alwaysShow = false,
  background = 'light',
  onClose = noop,
}: ActionButtonTooltipProps) => {
  const dataTipRef = useRef();

  useEffect(() => {
    if (alwaysShow) {
      ReactTooltip.show(dataTipRef.current);
    }
  }, [alwaysShow]);

  const backgroundColor = useMemo(() => {
    if (background === 'light') {
      return '#FFF'; // bg-neutral-full-white
    }
    return '#525A63'; // bg-neutral-darker
  }, [background]);

  if (!id) {
    return null;
  }

  const close = () => {
    ReactTooltip.hide(dataTipRef.current);
  };

  const spanAlwaysShow = alwaysShow
    ? {
        'data-event': 'no-event-manually-handled',
        'data-event-off': 'no-event-manually-handled',
        'data-tip': 'alwaysShown',
      }
    : {};

  return (
    <div className="z-50">
      <span data-tip ref={dataTipRef} data-for={id} {...spanAlwaysShow}>
        {dataTip}
      </span>
      <ReactTooltip
        id={id}
        place="top"
        clickable={alwaysShow || clickable}
        delayHide={alwaysShow ? 0 : delayHide}
        effect="solid"
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore-next-line
        offset={background === 'light' ? { left: '-92.5%' } : {}}
        className={clsx(
          '!w-64 !border-2 !p-4 !rounded !opacity-100  !whitespace-break-spaces z-50 ',
          {
            '!bg-neutral-darker !text-neutral-no-white': background === 'dark',
            '!bg-neutral-full-white !text-neutral-dark shadow-2xl react-tooltip-custom-arrow-offset':
              background === 'light',
          },
        )}
        backgroundColor={backgroundColor}
        border={true}
        borderClass="!border !border-gray-300"
        afterHide={onClose}
      >
        <div className="flex">
          {children}
          {alwaysShow && (
            <CloseIcon
              className=" w-6 h-6 cursor-pointer  relative -top-2 -right-1 text-neutral"
              onClick={close}
            ></CloseIcon>
          )}
        </div>
      </ReactTooltip>
    </div>
  );
};

ActionButtonTooltip.displayName = 'ActionButtonTooltip';
